import React from "react"
import {useTranslation} from "react-i18next"
import {Outlet, useMatch, useNavigate} from "react-router"

import {GenericErrorAlert} from "../../../components/Alert.tsx"
import {LayoutBlock} from "../../../components/Layout/LayoutBlock.tsx"
import {Loading} from "../../../components/Loading.tsx"
import {SellingsBlockBadge} from "../../../components/SellingsBlock"
import {TabItem, TabsContainer} from "../../../components/Tabs.tsx"
import {useSalesCycleDetailQuery} from "../../../queries/salesCycles.ts"
import {useUserSettingsOrLogout} from "../../../queries/user.ts"
import {AUserTypes} from "../../../services/types.generated.ts"
import {useNumParam} from "../../../utils/hooks.tsx"

export type TTab = "news" | "reports" | "prospects" | "details"

export const SellingDetails: React.FC = () => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()

  const navigate = useNavigate()

  const salesCycleId = useNumParam("salesCycleId")
  const match = useMatch("sellings/:salesCycleId/:tab/*")
  const tabParam = match?.params.tab as TTab | undefined

  const handleSelectTab = (tab: TTab) => {
    navigate(`/sellings/${salesCycleId}/${tab}`)
  }

  React.useEffect(() => {
    if (tabParam) {
      return
    }

    navigate(`/sellings/${salesCycleId}/news`, {replace: true})
  }, [salesCycleId, navigate, tabParam])

  const {data, error, isLoading, refetch} = useSalesCycleDetailQuery(salesCycleId)

  if (error) {
    return <GenericErrorAlert retry={refetch} />
  }

  if (isLoading || !data) {
    return <Loading size={"xl"} delayShow fullSpace />
  }

  return (
    <div className={"mb-8 flex grow flex-col"}>
      <LayoutBlock outerClassName={"pt-16"} innerClassName={"flex flex-col gap-9"}>
        <div className={"flex items-center justify-between gap-8"}>
          <h1 className={"text-4xl font-semibold text-cr-blue"}>{data.name}</h1>
          <SellingsBlockBadge isActive={true} status={data.status} />
        </div>

        <TabsContainer>
          <TabItem isActive={tabParam === "news"} onClick={() => handleSelectTab("news")}>
            {t("SellingDetails_Tabs_News")}
          </TabItem>
          <TabItem isActive={tabParam === "reports"} onClick={() => handleSelectTab("reports")}>
            {t("SellingDetails_Tabs_Reports")}
          </TabItem>
          <TabItem isActive={tabParam === "prospects"} onClick={() => handleSelectTab("prospects")}>
            {t("SellingDetails_Tabs_Prospects")}
          </TabItem>
          {user.type !== AUserTypes.SaasCompanyUser && (
            <TabItem isActive={tabParam === "details"} onClick={() => handleSelectTab("details")}>
              {t("SellingDetails_Tabs_Details")}
            </TabItem>
          )}
        </TabsContainer>
      </LayoutBlock>

      <React.Suspense fallback={<Loading size={"xl"} />}>
        <Outlet />
      </React.Suspense>
    </div>
  )
}
